import {
  ArrowLeft,
  Ban,
  FileText,
  LoaderCircle,
  PackageCheck,
  Pencil,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  useNavigate,
  useParams,
} from 'react-router-dom'

import ComparacionKilosCompraPrevia from '../../components/abastecimiento/ComparacionKilosCompraPrevia'
import { calcularDiferenciaKilos } from '../../components/abastecimiento/diferenciaCompraPrevia'
import CorregirRecepcionModal from '../../components/abastecimiento/CorregirRecepcionModal'
import RecepcionModal from '../../components/abastecimiento/RecepcionModal'
import AnularCompraModal from '../../components/compras/AnularCompraModal'
import EliminarCompraModal from '../../components/compras/EliminarCompraModal'
import ModalMensaje from '../../components/ui/ModalMensaje'
import { useAuth } from '../../context/AuthContext'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  cargarDetalleAbastecimiento,
  cargarPesajesCompra,
} from '../../services/abastecimiento'
import {
  compraAdmiteRecepcion,
  obtenerEstadoRecepcion,
  obtenerModalidad,
  obtenerMontoCompra,
  puedeAnularAbastecimiento,
  puedeCorregirRecepcion,
  puedeRegistrarRecepcion,
  puedeValidarRecepcion,
} from '../../utils/abastecimiento'
import {
  formatearEnteroCompra,
  formatearKilosCompra,
  formatearMontoCompra,
  nombreProveedorCompra,
  obtenerEstadoCompra,
  puedeAnularCompra,
  puedeEliminarCompra,
} from '../../utils/compras'
import { formatearFechaPos } from '../../utils/pos'

function Dato({
  etiqueta,
  valor,
  className = '',
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </dt>

      <dd className="mt-1 break-words text-sm font-semibold text-slate-900 sm:text-base">
        {valor || '—'}
      </dd>
    </div>
  )
}

function FilaResumen({
  etiqueta,
  valor,
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <dt className="text-sm text-slate-500">
        {etiqueta}
      </dt>

      <dd className="text-right text-sm font-semibold tabular-nums text-slate-900">
        {valor}
      </dd>
    </div>
  )
}

// tara_total incluye el pallet; la tara de envases es la diferencia.
function taraEnvasesKg(pesaje) {
  return (
    Math.round(
      (Number(pesaje.tara_total_kg) - Number(pesaje.pallet_tara_kg ?? 0)) *
        1000
    ) / 1000
  )
}

function DetalleCompra() {
  const { compraId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal } = useModalMensaje()

  const empresaId = empresaActual?.id
  const rol = empresaActual?.rol
  const esAdministrador = rol === 'administrador'
  const esConsulta = !puedeRegistrarRecepcion(rol)

  const [compra, setCompra] = useState(null)
  const [recepcion, setRecepcion] = useState(null)
  const [bodega, setBodega] = useState(null)
  const [ajustes, setAjustes] = useState([])
  const [factura, setFactura] = useState(null)
  const [recepcionAbierta, setRecepcionAbierta] = useState(false)
  const [correccionAbierta, setCorreccionAbierta] = useState(false)
  const [pesajes, setPesajes] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [anulacionAbierta, setAnulacionAbierta] = useState(false)
  const [anulando, setAnulando] = useState(false)
  const [eliminacionAbierta, setEliminacionAbierta] = useState(false)

  const cargarCompra = useCallback(async () => {
    if (!empresaId || !compraId) return

    setCargando(true)
    setError('')

    try {
      const [detalle, listaPesajes] = await Promise.all([
        cargarDetalleAbastecimiento(empresaId, compraId),
        cargarPesajesCompra(empresaId, compraId),
      ])

      if (!detalle) {
        setCompra(null)
        setPesajes([])
        setError('La compra no existe o pertenece a otra empresa.')
        return
      }

      setCompra(detalle.compra)
      setRecepcion(detalle.recepcion)
      setBodega(detalle.bodega)
      setAjustes(detalle.ajustes)
      setFactura(detalle.factura)
      setPesajes(listaPesajes)
    } catch (errorCarga) {
      console.error('Error al cargar compra:', errorCarga)
      setError(
        errorCarga.message || 'No fue posible cargar la compra.'
      )
    } finally {
      setCargando(false)
    }
  }, [empresaId, compraId])

  useEffect(() => {
    cargarCompra()
  }, [cargarCompra])

  const confirmarAnulacion = async (motivo) => {
    setAnulando(true)

    try {
      const { error: errorAnular } = await supabase.rpc('anular_compra', {
        p_empresa_id: empresaId,
        p_compra_id: compra.id,
        p_motivo: motivo,
      })

      if (errorAnular) throw errorAnular

      setAnulacionAbierta(false)

      abrirModal({
        tipo: 'success',
        titulo: 'Compra anulada',
        mensaje: `La compra ${compra.codigo_compra} fue anulada correctamente.`,
      })

      await cargarCompra()
    } catch (errorAnular) {
      console.error('Error al anular compra:', errorAnular)

      setAnulacionAbierta(false)

      abrirModal({
        tipo: 'error',
        titulo: 'No fue posible anular la compra',
        mensaje:
          errorAnular.message ||
          'Ocurrió un error al anular la compra.',
      })
    } finally {
      setAnulando(false)
    }
  }

  const botonVolver = (
    <button
      type="button"
      onClick={() => navigate('/compras')}
      className="mb-5 flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
    >
      <ArrowLeft size={18} />
      Volver a compras
    </button>
  )

  if (cargando && !compra) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={36}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando compra...
          </p>
        </div>
      </div>
    )
  }

  if (error || !compra) {
    return (
      <div className="w-full">
        {botonVolver}

        <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h3 className="text-lg font-bold text-red-600">
            No fue posible mostrar la compra
          </h3>

          <p className="mt-2 text-slate-600">
            {error || 'La compra no existe.'}
          </p>

          <button
            type="button"
            onClick={cargarCompra}
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            <RefreshCw size={18} />
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  const estado = obtenerEstadoCompra(compra.estado)
  const anulada = compra.estado === 'anulada'
  const modalidad = obtenerModalidad(compra.modalidad)
  const estadoRecepcion = obtenerEstadoRecepcion(recepcion)
  const monto = obtenerMontoCompra(compra, recepcion)
  const esPrevia = compra.modalidad === 'PREVIA'
  const historica = compra.recepcion_automatica_habilitada === false
  const puedeAnular =
    puedeAnularCompra(compra, {
      esAdministrador,
      esConsulta,
      usuarioId: user?.id,
    }) && puedeAnularAbastecimiento(compra, recepcion)
  const puedeRecibir =
    !esConsulta && compraAdmiteRecepcion(compra, recepcion)
  const puedeCorregir =
    puedeCorregirRecepcion(rol) && recepcion?.estado === 'VALIDADA'

  const comparacionKilos = calcularDiferenciaKilos({
    modalidad: compra.modalidad,
    acordadoKg: compra.cantidad_acordada_kg,
    brutoKg: compra.kilos_brutos_recepcion_kg ?? compra.peso_bruto_kg,
    netoKg: compra.peso_neto_kg,
    taraKg: compra.tara_total_kg,
    aceptadosKg: recepcion?.kg_aceptados,
    estadoRecepcion: recepcion?.estado,
  })
  const mostrarComparacion =
    esPrevia && !historica && (recepcion || !anulada)

  const alGuardarRecepcion = (mensaje) => {
    setRecepcionAbierta(false)
    abrirModal({
      tipo: 'success',
      titulo: 'Recepción',
      mensaje,
    })
    cargarCompra()
  }

  const alCorregirRecepcion = () => {
    setCorreccionAbierta(false)
    abrirModal({
      tipo: 'success',
      titulo: 'Recepción corregida',
      mensaje:
        'Se registró un movimiento compensatorio de inventario con el motivo indicado.',
    })
    cargarCompra()
  }

  return (
    <div className="w-full">
      {botonVolver}

      <div className="mb-6 flex flex-col justify-between gap-4 sm:mb-8 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Abastecimiento
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
              {compra.codigo_compra}
            </h2>

            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${modalidad.clase}`}
            >
              {modalidad.etiqueta}
            </span>

            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
            >
              {estado.etiqueta}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          {puedeAnular && (
            <button
              type="button"
              onClick={() => setAnulacionAbierta(true)}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-5 py-3 font-semibold text-red-600 transition hover:bg-red-50 sm:w-auto"
            >
              <Ban size={18} />
              Anular compra
            </button>
          )}

          {puedeEliminarCompra(rol) && (
            <button
              type="button"
              onClick={() => setEliminacionAbierta(true)}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-5 py-3 font-semibold text-red-600 transition hover:bg-red-50 sm:w-auto"
            >
              <Trash2 size={18} />
              Eliminar
            </button>
          )}
        </div>
      </div>

      {anulada && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-semibold">
            Compra anulada
            {compra.anulado_en &&
              ` el ${new Date(compra.anulado_en).toLocaleString('es-CL')}`}
          </p>

          {compra.motivo_anulacion && (
            <p className="mt-1 break-words">
              Motivo: {compra.motivo_anulacion}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <dl className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3">
              <Dato
                etiqueta="N° compra"
                valor={compra.codigo_compra}
              />

              <Dato
                etiqueta="Fecha"
                valor={formatearFechaPos(compra.fecha_compra)}
              />

              <Dato
                etiqueta="Precio/kg"
                valor={formatearMontoCompra(compra.precio_kilo)}
              />

              <Dato
                etiqueta="Proveedor / Agricultor"
                valor={nombreProveedorCompra(compra)}
                className="col-span-2 md:col-span-1"
              />

              <Dato
                etiqueta="Producto"
                valor={compra.producto_nombre}
              />

              <Dato
                etiqueta="Documento"
                valor={compra.documento}
              />

              {esPrevia && (
                <Dato
                  etiqueta="Kilos acordados"
                  valor={formatearKilosCompra(compra.cantidad_acordada_kg)}
                />
              )}

              <Dato
                etiqueta="Condición de pago"
                valor={
                  compra.condicion_pago
                    ? `${compra.condicion_pago}${
                        compra.plazo_dias ? ` · ${compra.plazo_dias} días` : ''
                      }`
                    : null
                }
              />

              {compra.observaciones && (
                <Dato
                  etiqueta="Observaciones"
                  valor={compra.observaciones}
                  className="col-span-2 md:col-span-3"
                />
              )}
            </dl>
          </section>

          {/* RECEPCIÓN */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Recepción
                </h3>

                {!historica && compra.estado !== 'anulada' && (
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoRecepcion.clase}`}
                  >
                    {estadoRecepcion.etiqueta}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {puedeRecibir && (
                  <button
                    type="button"
                    onClick={() => setRecepcionAbierta(true)}
                    className="flex min-h-11 items-center gap-2 rounded-lg border border-emerald-600 px-4 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
                  >
                    <PackageCheck size={16} />
                    {recepcion ? 'Editar / validar' : 'Registrar recepción'}
                  </button>
                )}

                {puedeCorregir && (
                  <button
                    type="button"
                    onClick={() => setCorreccionAbierta(true)}
                    className="flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    <Pencil size={16} />
                    Corregir kilos
                  </button>
                )}
              </div>
            </div>

            {historica ? (
              <p className="text-sm text-slate-500">
                Compra histórica: no genera recepción ni ingreso automático
                a inventario.
              </p>
            ) : !recepcion ? (
              <p className="text-sm text-slate-500">
                {compra.estado === 'anulada'
                  ? 'La compra está anulada.'
                  : 'Aún no hay recepción registrada para esta compra.'}
              </p>
            ) : (
              <dl className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <Dato
                  etiqueta="Bodega"
                  valor={bodega?.nombre}
                />

                <Dato
                  etiqueta="Fecha recepción"
                  valor={formatearFechaPos(recepcion.fecha_recepcion)}
                />

                <Dato
                  etiqueta="Kg netos recibidos"
                  valor={formatearKilosCompra(compra.peso_neto_kg)}
                />

                {recepcion.estado !== 'BORRADOR' && (
                  <Dato
                    etiqueta="Kg aceptados"
                    valor={formatearKilosCompra(recepcion.kg_aceptados)}
                  />
                )}

                {recepcion.motivo_rechazo && (
                  <Dato
                    etiqueta="Motivo del rechazo"
                    valor={recepcion.motivo_rechazo}
                    className="col-span-2"
                  />
                )}

                {recepcion.observacion_calidad && (
                  <Dato
                    etiqueta="Observación de calidad"
                    valor={recepcion.observacion_calidad}
                    className="col-span-2 md:col-span-3"
                  />
                )}
              </dl>
            )}

            {ajustes.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Correcciones ({ajustes.length})
                </h4>

                <ul className="space-y-2">
                  {ajustes.map((ajuste) => (
                    <li
                      key={ajuste.id}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <p className="font-semibold tabular-nums text-slate-900">
                        {formatearKilosCompra(ajuste.kg_antes)} →{' '}
                        {formatearKilosCompra(ajuste.kg_despues)}
                        <span className="ml-2 text-xs font-normal text-slate-400">
                          {new Date(ajuste.ajustado_en).toLocaleString('es-CL')}
                        </span>
                      </p>

                      <p className="break-words text-slate-600">
                        {ajuste.motivo}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {mostrarComparacion && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Comparación de kilos
              </h3>

              <ComparacionKilosCompraPrevia
                comparacion={comparacionKilos}
              />
            </section>
          )}

          {/* FACTURA */}
          {(factura || recepcion?.estado === 'RECHAZADA_TOTAL') && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Factura del proveedor
              </h3>

              {factura ? (
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <Dato
                      etiqueta="Documento"
                      valor={`${
                        factura.tipo_documento === 'factura_exenta'
                          ? 'Fact. exenta'
                          : 'Factura'
                      } ${factura.numero_documento}`}
                    />

                    <Dato
                      etiqueta="Fecha"
                      valor={formatearFechaPos(factura.fecha_factura)}
                    />

                    <Dato
                      etiqueta="Total factura"
                      valor={formatearMontoCompra(factura.monto_total)}
                    />

                    <Dato
                      etiqueta="Vencimiento"
                      valor={
                        factura.fecha_vencimiento
                          ? formatearFechaPos(factura.fecha_vencimiento)
                          : 'Contado'
                      }
                    />
                  </dl>

                  {factura.gasto_id && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/gastos?ver=${factura.gasto_id}`)
                      }
                      className="flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                    >
                      <FileText size={16} />
                      Ver gasto
                    </button>
                  )}
                </div>
              ) : (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Recepción rechazada totalmente: si el proveedor emite
                  factura, requiere revisión administrativa en Gastos. No se
                  genera gasto automático.
                </p>
              )}
            </section>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Detalle de pesaje ({pesajes.length})
            </h3>

            {/* Mobile: tarjetas */}
            <div className="space-y-3 md:hidden">
              {pesajes.map((pesaje) => (
                <div
                  key={pesaje.id}
                  className="rounded-xl border border-slate-200 p-4"
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Pesaje {pesaje.numero_pesaje}
                  </p>

                  <p className="mt-1 font-semibold text-slate-900">
                    {pesaje.cantidad_cajas} cajas · {pesaje.envase_nombre}
                  </p>

                  <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div className="min-w-0">
                      <dt className="text-xs text-slate-500">Bruto</dt>
                      <dd className="font-semibold tabular-nums text-slate-800">
                        {formatearKilosCompra(pesaje.peso_bruto_kg)}
                      </dd>
                    </div>

                    <div className="min-w-0">
                      <dt className="text-xs text-slate-500">
                        Tara envases
                      </dt>
                      <dd className="font-semibold tabular-nums text-slate-800">
                        {formatearKilosCompra(taraEnvasesKg(pesaje))}
                      </dd>
                    </div>

                    <div className="min-w-0">
                      <dt className="text-xs text-slate-500">
                        Peso pallet
                      </dt>
                      <dd className="font-semibold tabular-nums text-slate-800">
                        {formatearKilosCompra(pesaje.pallet_tara_kg ?? 0)}
                      </dd>
                    </div>

                    <div className="min-w-0">
                      <dt className="text-xs text-slate-500">Neto</dt>
                      <dd className="font-bold tabular-nums text-emerald-700">
                        {formatearKilosCompra(pesaje.peso_neto_kg)}
                      </dd>
                    </div>
                  </dl>

                  {pesaje.observacion && (
                    <p className="mt-2 break-words text-sm text-slate-500">
                      {pesaje.observacion}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Tablet / desktop: tabla */}
            <div className="hidden w-full overflow-x-auto md:block">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">Pesaje</th>
                    <th className="py-2 pr-3 text-right">Cajas</th>
                    <th className="py-2 pr-3">Caja/Rejilla</th>
                    <th className="py-2 pr-3 text-right">Peso bruto</th>
                    <th className="py-2 pr-3 text-right">Tara envases</th>
                    <th className="py-2 pr-3 text-right">Peso pallet</th>
                    <th className="py-2 pr-3 text-right">Peso neto</th>
                    <th className="py-2">Observación</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {pesajes.map((pesaje) => (
                    <tr key={pesaje.id}>
                      <td className="py-2 pr-3 font-semibold text-slate-700">
                        {pesaje.numero_pesaje}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {pesaje.cantidad_cajas}
                      </td>
                      <td className="py-2 pr-3 text-slate-700">
                        {pesaje.envase_nombre}
                        <span className="ml-1 text-xs text-slate-400">
                          ({formatearKilosCompra(pesaje.tara_unitaria_kg)} c/u)
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                        {formatearKilosCompra(pesaje.peso_bruto_kg)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-slate-500">
                        {formatearKilosCompra(taraEnvasesKg(pesaje))}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-slate-500">
                        {formatearKilosCompra(pesaje.pallet_tara_kg ?? 0)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-right font-semibold tabular-nums text-slate-900">
                        {formatearKilosCompra(pesaje.peso_neto_kg)}
                      </td>
                      <td className="max-w-[14rem] break-words py-2 text-slate-500">
                        {pesaje.observacion || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="min-w-0">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6 lg:sticky lg:top-28">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Resumen
            </h3>

            <dl className="mt-3 divide-y divide-slate-100">
              <FilaResumen
                etiqueta="Pesajes"
                valor={formatearEnteroCompra(compra.cantidad_pesajes)}
              />

              <FilaResumen
                etiqueta="Cajas"
                valor={formatearEnteroCompra(compra.cantidad_cajas)}
              />

              <FilaResumen
                etiqueta="Peso bruto"
                valor={formatearKilosCompra(compra.peso_bruto_kg)}
              />

              <FilaResumen
                etiqueta="Tara"
                valor={formatearKilosCompra(compra.tara_total_kg)}
              />

              <FilaResumen
                etiqueta="Peso neto"
                valor={formatearKilosCompra(compra.peso_neto_kg)}
              />

              <FilaResumen
                etiqueta="Precio/kg"
                valor={formatearMontoCompra(compra.precio_kilo)}
              />
            </dl>

            <div
              className={`mt-3 flex items-center justify-between gap-4 rounded-xl px-4 py-3 ${
                anulada ? 'bg-slate-100' : 'bg-emerald-50'
              }`}
            >
              <span
                className={`text-sm font-bold uppercase tracking-wide ${
                  anulada ? 'text-slate-500' : 'text-emerald-800'
                }`}
              >
                {monto.etiqueta}
              </span>

              <span
                className={`text-right text-xl font-bold tabular-nums ${
                  anulada
                    ? 'text-slate-500 line-through'
                    : monto.definitivo
                      ? 'text-emerald-700'
                      : 'text-slate-600'
                }`}
              >
                {formatearMontoCompra(monto.valor)}
              </span>
            </div>
          </section>
        </aside>
      </div>

      <RecepcionModal
        abierto={recepcionAbierta}
        compra={recepcionAbierta ? compra : null}
        recepcion={recepcion}
        empresaId={empresaId}
        puedeValidar={puedeValidarRecepcion(rol)}
        onCerrar={() => setRecepcionAbierta(false)}
        onGuardada={alGuardarRecepcion}
      />

      <CorregirRecepcionModal
        abierto={correccionAbierta}
        compra={compra}
        recepcion={recepcion}
        empresaId={empresaId}
        onCerrar={() => setCorreccionAbierta(false)}
        onCorregida={alCorregirRecepcion}
      />

      <AnularCompraModal
        abierto={anulacionAbierta}
        compra={compra}
        procesando={anulando}
        onCancelar={() => setAnulacionAbierta(false)}
        onConfirmar={confirmarAnulacion}
      />

      <EliminarCompraModal
        compra={eliminacionAbierta ? compra : null}
        empresaId={empresaId}
        onCancelar={() => setEliminacionAbierta(false)}
        onEliminada={(resultado, preview) => {
          const codigo = preview?.codigo_compra || compra.codigo_compra

          // Se limpia el estado local y se vuelve al listado, que avisa
          // el éxito y recarga todo desde Supabase.
          setEliminacionAbierta(false)
          setCompra(null)
          setRecepcion(null)
          setBodega(null)
          setAjustes([])
          setFactura(null)
          setPesajes([])
          navigate('/compras', {
            replace: true,
            state: { compraEliminada: codigo },
          })
        }}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

export default DetalleCompra
